
import Container from "~/components/atoms/Container";
import { useState, lazy, Suspense, useCallback } from "react";
import CustomPagination from "~/components/blocks/Pagination/Pagination";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/custom-radix-components/pagination";

import { Skeleton } from "~/components/ui/skeleton";
import BlogSkeletonCard from "../Card/BlogSkeletonCard";
import BlogsFilterChips from "../Chips/BlogsFilterChips";
import BlogHeroBanner from "../HeroBanners/BlogHeroBanner";
import SubscribeBanner from "../SubscribeBanner/SubscribeBanner";
import PodcastSection from "../Sections/PodcastSection";
import TrainingVideosScection from "../Sections/TrainingVideosScection";
import TenantHelpGuide from "../Sections/TenantHelpGuide";
// import AreaGuidesSection from "../Sections/AreaGuidesSection";
import { Await, Link } from "@remix-run/react";
import BlogCategoriesSection from "../Sections/BlogCategoriesSection";
import ButtonSearchbar from "../Searchbars/ButtonSearchbar";
import GuideCard from "../Card/GuideCard";
import { getRegionUrlPrefix } from "~/lib/helpers/slugHelper";
import { DEFAULT_REGION } from "~/lib/constants/generalConstants";
import BlogCard from "../Card/BlogCard";
import { LazyLoader } from "~/lib/utils/LazyLoader";
import OfficeSearchBanner from "../common/OfficeBanner";
import NoResults from "../Card/NoResultCard";
import { buildBlogChipTopics } from "~/lib/constants/blogFilterChips";


const Breadcrumbs = lazy(() => import("~/components/blocks/BreadCrumbs"));


interface DesktopLayoutProps {
  getBlogs: (categories: string[], searchText: string, page?: number) => void;
  loading: boolean;
  renderBlogs: () => JSX.Element | JSX.Element[];
  country: string;
  params: any;
  content: any;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  media: any;
  searchText: string;
  blogsData: any;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  region: string;
  currentMedia?: any;
  setCurrentMedia?: (media: any) => void;
}

export default function DeskyopLayout({
  getBlogs,
  loading,
  renderBlogs,
  country,
  params,
  content,
  onSearch,
  media,
  searchText,
  blogsData,
  currentPage,
  setCurrentPage,
  region,
}: DesktopLayoutProps) {

  const [selected] = useState<string[]>(['blog']);
  const ITEMS_PER_PAGE = 12;
  const totalPages = Math.ceil(blogsData?.totalBlogs / ITEMS_PER_PAGE);
  const chipTopics = buildBlogChipTopics(country);



  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    getBlogs([], searchText, newPage);
  }, [totalPages, searchText, getBlogs]);


  const clearFilters = useCallback(() => {
    setCurrentPage(1);

    if (onSearch) {
      onSearch({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
    }
    getBlogs([], "", 1);
  }, [getBlogs, onSearch]);

  return (
    <>
      <Container htmlFor='ultra-wide'>
        <Suspense fallback={<Skeleton className="h-6 w-32" />}>
          <Breadcrumbs params={{ country: region || DEFAULT_REGION }} className="hidden md:block mt-4" />
        </Suspense>
        <div className="flex items-center justify-between gap-4 mb-2">
          <h1 className="text-xl sm:text-2xl text-oh-black font-semibold line-clamp-2 shrink">
            {`News & Insights on Flexible Office Spaces in ${country === "United States" || country === "United Kingdom" ? `the ${country}` : country
              }`}
          </h1>
          <div className="w-full max-w-2xl shrink-0 [&>div]:mb-0">
            <ButtonSearchbar btnText={'Search'} onSearch={onSearch} value={searchText} className="max-w-none" />
          </div>
        </div>

        {/* Hero Section */}
        {/* {content['featured-blogs'][0] && (
          <BlogHeroBanner mainArticle={content['featured-blogs'][0]} isFeaturedAvailable featuredPosts={content['featured-blogs']?.slice(1)} country={country} />
        )} */}
        {/* Search and Topics */}
        <div className="w-full py-6 ">

          <BlogsFilterChips className="mb-4" selected={selected} topics={chipTopics} />


          {/* Blog Grid */}
          {blogsData === null || loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 my-4">
              {Array.from({ length: ITEMS_PER_PAGE }, (_, idx) => (
                <BlogSkeletonCard displayMode='grid' />
              ))}
            </div>
          ) : blogsData.blogs.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 my-4">
                {blogsData.blogs.map((article: any, idx: number) => (
                  <BlogCard country={country} key={article.id || idx} article={article} />
                ))}
              </div>

              {/* Pagination */}
              {Math.ceil(blogsData.pageInfo.totalCount / ITEMS_PER_PAGE) > 1 && (
                <div className="flex justify-center my-4">
                  <Pagination className="max-w-[550px] w-full justify-center">
                    <PaginationContent className="flex items-center">
                      {currentPage > 1 ? (
                        <PaginationItem className="cursor-pointer">
                          <PaginationPrevious
                            to="#"
                            onClick={() => handlePageChange(currentPage - 1)}
                          />
                        </PaginationItem>
                      ) : (
                        <div className="w-[60px]" />
                      )}

                      <CustomPagination
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                        totalPages={Math.ceil(
                          blogsData.pageInfo.totalCount / ITEMS_PER_PAGE
                        )}
                      />

                      {currentPage <
                        Math.ceil(blogsData.pageInfo.totalCount / ITEMS_PER_PAGE) ? (
                        <PaginationItem className="cursor-pointer">
                          <PaginationNext
                            to="#"
                            onClick={() => handlePageChange(currentPage + 1)}
                          />
                        </PaginationItem>
                      ) : (
                        <div className="w-[40px]" />
                      )}
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="py-5">
              <NoResults onClear={clearFilters} />
            </div>
          )}

          {content['tenant-guide']?.length ?
            <TenantHelpGuide guides={content['tenant-guide']} country={country} /> : null
          }
          {content['host-guide']?.length ? <section>
            <div className="flex justify-between items-end my-6">
              <h3 className="text-lg font-semibold leading-none">Host Hub</h3>
              <div className="hidden md:block h-[2px] bg-[#FFBC4B] flex-1 mx-4" />
              <Link
                to={`${getRegionUrlPrefix(country)}/blog/workspace-guides`}
                className="hidden md:block text-sm text-[#FFBC4B]  hover:underline leading-none"
              >
                See more host guides
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content['host-guide'].map((article: any, idx: number) => (
                <GuideCard country={country} key={idx} article={article} />
              ))}
            </div>
          </section> : null}
          {/* Media Sections (deferred) */}
          <Suspense fallback={<Skeleton className="min-h-80" />}>
            <Await resolve={media}>
              {(resolvedMedia) => (
                <LazyLoader>
                  {resolvedMedia?.video?.length ? (
                    <TrainingVideosScection media={resolvedMedia.video} country={country} />
                  ) : null}

                  {/* {content["area-guide"]?.length ? (
                    <AreaGuidesSection
                      guides={content["area-guide"]}
                      params={params}
                      country={country}
                    />
                  ) : null} */}

                  {resolvedMedia?.audio?.length ? (
                    <PodcastSection media={resolvedMedia.audio || []} country={country} />
                  ) : null}
                </LazyLoader>
              )}
            </Await>
          </Suspense>
        </div>
        <BlogCategoriesSection />

      </Container>
      {/* <SubscribeBanner /> */}
      <div className='pt-10'>
        <OfficeSearchBanner className='h-96 bg-oh-cyan mt-10' country={country} />
      </div>
    </>
  );
}
